<template>
  <span>
    <v-card class="my-1 elevation-0">
      <v-card-title>
        <div>
          <h5>PSFP por Serviço — Série Temporal</h5>
          <div class="caption grey--text">Anual (acumulado) e Mensal</div>
        </div>
        <v-spacer />
        <v-select
          :value="servicoSelecionado"
          :items="opcoesFormatadas"
          item-text="label"
          item-value="codigo"
          dense
          outlined
          hide-details
          style="max-width: 280px;"
          :disabled="loadingOpcoes"
          @input="setServicoSelecionado"
        />
      </v-card-title>
      <v-card-text>
        <div
          v-if="loadingSerie"
          class="d-flex align-center justify-center"
          style="height: 320px;"
        >
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>
        <div
          v-show="!loadingSerie"
          ref="chartContainer"
          style="width: 100%; height: 320px;"
        />
        <div
          v-if="!loadingSerie && !serieAtual && servicoSelecionado"
          class="text-center grey--text py-4"
        >
          Sem dados para exibição
        </div>
      </v-card-text>
    </v-card>
  </span>
</template>

<script>
import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
import DashboardIsfpPsfpService from "@/services/DashboardIsfpPsfpService";
import { highchartsColors } from "@/constants/highchartsColors";
import { mapState, mapActions } from "vuex";

HighchartsNoData(Highcharts);

export default {
  mounted() {
    this.buscarSerie();
  },
  name: "DashboardIsfpPsfpSerieTemporalPsfpTendencia",
  props: {
    opcoesServicos: { type: Array, default: () => [] },
    loadingOpcoes: { type: Boolean, default: false },
  },
  data: () => ({
    serieAtual: null,
    loadingSerie: false,
    chartInstance: null,
  }),
  computed: {
    ...mapState("parametrosDashboardIsfpPsfp", ["servicoSelecionado"]),
    opcoesFormatadas() {
      return this.opcoesServicos.map((s) => ({
        codigo: s.codigos_servico,
        label: `${s.indice_categoria} — ${s.descricao_por_indice}`,
      }));
    },
  },
  watch: {
    opcoesServicos(val) {
      if (val.length > 0) {
        this.iniciar();
      }
    },
    servicoSelecionado(val) {
      if (val) {
        this.buscarSerie();
      }
    },
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  },
  methods: {
    ...mapActions("parametrosDashboardIsfpPsfp", ["setServicoSelecionado"]),
    iniciar() {
      if (this.opcoesServicos.length > 0 && !this.servicoSelecionado) {
        this.setServicoSelecionado(this.opcoesServicos[0].codigos_servico);
      }
    },

    async buscarSerie() {
      if (!this.servicoSelecionado) return;

      this.loadingSerie = true;
      this.serieAtual = null;

      try {
        const dados = await DashboardIsfpPsfpService.getDadosTela2SeriePsfp(
          this.servicoSelecionado,
        );
        this.serieAtual = dados;
        this.$nextTick(() => this.renderChart());
      } catch {
        this.$toast.error("Erro ao carregar série do serviço.", "", {
          position: "topRight",
        });
      } finally {
        this.loadingSerie = false;
      }
    },

    renderChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const container = this.$refs.chartContainer;
      const servico = this.serieAtual;
      if (!container || !servico) return;

      const categorias = servico.serie.map((p) =>
        this.formatarCompetencia(p.competencia),
      );
      const dadosSa = servico.serie.map((p) => p.sa ?? 0);
      const dadosPsfpMensal = servico.serie.map((p) => p.psfp ?? null);
      const dadosPsfpAnual = servico.serie.map((p) => p.psfp_anual ?? null);
      const nomeServico = servico.descricao_por_indice;

      const dadosSfp = servico.serie.map((p) => p.sfp ?? 0);

      this.chartInstance = Highcharts.chart(container, {
        chart: {
          style: { fontFamily: "inherit" },
        },
        title: { text: null },
        xAxis: {
          categories: categorias,
          crosshair: true,
        },
        yAxis: [
          {
            title: { text: null },
            min: 0,
          },
          {
            title: { text: null },
            labels: {
              formatter() {
                return this.value + "%";
              },
            },
            opposite: true,
            min: 0,
          },
        ],
        tooltip: {
          shared: true,
          formatter() {
            let s = `<b>${this.x}</b><br/>${nomeServico}<br/><br/>`;
            this.points.forEach((point) => {
              if (point.series.name === "SFP" || point.series.name === "SA") {
                s += `${point.series.name}: <b>${point.y.toLocaleString(
                  "pt-BR",
                )}</b><br/>`;
              } else {
                const valor = point.y !== null ? point.y + "%" : "—";
                s += `${point.series.name}: <b>${valor}</b><br/>`;
              }
            });
            return s;
          },
        },
        legend: {
          enabled: true,
          align: "left",
          verticalAlign: "bottom",
          symbolWidth: 28,
        },
        series: [
          {
            name: "SA",
            type: "column",
            data: dadosSa,
            color: highchartsColors[0],
            yAxis: 0,
          },
          {
            name: "PSFP Anual",
            type: "line",
            data: dadosPsfpAnual,
            color: highchartsColors[1],
            dashStyle: "ShortDash",
            lineWidth: 2.5,
            marker: {
              radius: 4,
              symbol: "circle",
              fillColor: highchartsColors[1],
            },
            yAxis: 1,
            zIndex: 2,
          },
          {
            name: "PSFP Mensal",
            type: "line",
            data: dadosPsfpMensal,
            color: highchartsColors[2],
            lineWidth: 2.5,
            marker: { radius: 4, symbol: "circle" },
            yAxis: 1,
            zIndex: 3,
          },
          {
            name: "SFP",
            type: "line",
            data: dadosSfp,
            color: "transparent",
            lineWidth: 0,
            marker: { enabled: false },
            showInLegend: false,
            enableMouseTracking: true,
            yAxis: 0,
          },
        ],
        credits: { enabled: false },
        lang: { noData: "Sem dados para exibição" },
        noData: { style: { color: "#999", fontSize: "14px" } },
      });
    },

    formatarCompetencia(competencia) {
      const [ano, mes] = competencia.split("-");
      const meses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      return `${meses[parseInt(mes) - 1]}/${ano.slice(2)}`;
    },
  },
};
</script>

<style scoped></style>
